import React, { useState, useCallback } from "react";
import { Box, Typography, Paper, Button, IconButton, alpha } from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  Add as AddIcon,
} from "@mui/icons-material";

interface PublishSuccessModalProps {
  siteUrl: string;
  customDomain?: string | null;
  onClose: () => void;
  onAddCustomDomain?: () => void;
}

export const PublishSuccessModal: React.FC<PublishSuccessModalProps> = ({
  siteUrl,
  customDomain,
  onClose,
  onAddCustomDomain,
}) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  const fullUrl = `https://${siteUrl}`;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1500,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.6)",
          backdropFilter: "blur(4px)",
        }}
      />

      {/* Modal */}
      <Paper
        elevation={24}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 450,
          overflow: "hidden",
          borderRadius: 3,
          animation: "fadeIn 0.3s ease-out",
          "@keyframes fadeIn": {
            from: { opacity: 0, transform: "scale(0.9)" },
            to: { opacity: 1, transform: "scale(1)" },
          },
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "text.secondary",
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Success Icon & Message */}
        <Box
          sx={(theme) => ({
            pt: 5,
            pb: 3,
            px: 3,
            textAlign: "center",
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          })}
        >
          <Box
            sx={(theme) => ({
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: alpha(theme.palette.success.main, 0.15),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            })}
          >
            <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
          </Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Site Published!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your site is now live and accessible to everyone
          </Typography>
        </Box>

        {/* URL Section */}
        <Box sx={{ p: 3 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: "block", mb: 1 }}>
            Your site URL
          </Typography>
          <Box
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderRadius: 2,
              border: 1,
              borderColor: alpha(theme.palette.primary.main, 0.2),
              mb: 2,
            })}
          >
            <Typography
              variant="body1"
              fontWeight={600}
              sx={{
                fontFamily: "monospace",
                color: "primary.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {siteUrl}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => copyToClipboard(fullUrl)}
                sx={{ color: copied ? "success.main" : "primary.main" }}
              >
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton size="small" onClick={() => window.open(fullUrl, "_blank")} sx={{ color: "primary.main" }}>
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
          {copied && (
            <Typography variant="caption" color="success.main" sx={{ display: "block", mb: 1, mt: -1 }}>
              URL copied to clipboard!
            </Typography>
          )}

          {/* Custom Domain Section */}
          {customDomain ? (
            <Box
              sx={(theme) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: alpha(theme.palette.success.main, 0.04),
                borderRadius: 2,
                border: 1,
                borderColor: alpha(theme.palette.success.main, 0.2),
                mb: 2,
              })}
            >
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                  {customDomain}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Custom domain
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => window.open(`https://${customDomain}`, "_blank")}
                sx={{ color: "success.main" }}
              >
                <OpenInNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          ) : (
            onAddCustomDomain && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  onClose();
                  onAddCustomDomain();
                }}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  borderStyle: "dashed",
                  mb: 2,
                }}
              >
                Add Custom Domain
              </Button>
            )
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => window.open(fullUrl, "_blank")}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 1.25,
              }}
            >
              Visit Site
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 1.25,
              }}
            >
              Done
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
